import { VIDEO_RECORDING, getSupportedMimeType } from './constants';
import ShipmentRecorder from './ShipmentRecorder';
import VideoUploader from './VideoUploader';

export default class VideoRecorderService {
  constructor(recordingApi, { onUploadSuccess } = {}) {
    this.stream = null;
    this.mimeType = null;
    this.standbyRecorder = null;
    this.activeRecorder = null;
    this._standbyTimer = null;
    this.uploader = new VideoUploader(recordingApi);
    this.onUploadSuccess = onUploadSuccess || null;
    this.initialized = false;
    this.error = null;
  }

  async initialize() {
    if (this.initialized) return;
    this.error = null;

    this.mimeType = getSupportedMimeType();
    if (!this.mimeType) {
      this.error = 'MediaRecorder not supported';
      return;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: VIDEO_RECORDING.VIDEO_CONSTRAINTS,
        audio: false,
      });
    } catch (err) {
      this.error = 'Camera permission denied';
      return;
    }

    this._startStandbyRecorder();
    this.initialized = true;
  }

  onShipmentStart(shipmentId) {
    if (!this.initialized) return;

    if (this.activeRecorder) {
      this.activeRecorder.abort();
      this.activeRecorder = null;
    }

    this._clearStandbyTimer();

    if (this.standbyRecorder) {
      this.standbyRecorder.shipmentId = shipmentId;
      this.activeRecorder = this.standbyRecorder;
      this.standbyRecorder = null;
    } else {
      try {
        const recorder = new ShipmentRecorder(
          this.stream,
          this.mimeType,
          shipmentId,
        );
        recorder.start();
        this.activeRecorder = recorder;
      } catch (err) {
        console.error('Failed to start shipment recorder:', err);
      }
    }
  }

  async onShipmentEnd(shipmentId) {
    if (!this.initialized || !this.activeRecorder) return;
    if (this.activeRecorder.shipmentId !== shipmentId) return;

    const recorder = this.activeRecorder;
    this.activeRecorder = null;

    this._startStandbyRecorder();

    try {
      const blob = await recorder.stopWithDelay();
      const durationMs = recorder.getDurationMs();

      if (blob) {
        this.uploader
          .upload(shipmentId, blob, durationMs)
          .then(() => {
            this.onUploadSuccess?.(shipmentId);
          })
          .catch(err => {
            console.error(
              `[VideoRecorder] Upload pipeline failed for shipment ${shipmentId}:`,
              err,
            );
          });
      }
    } catch (err) {
      console.error('Failed to stop shipment recorder:', err);
    }
  }

  onShipmentSidelined() {
    if (!this.initialized) return;

    if (this.activeRecorder) {
      this.activeRecorder.abort();
      this.activeRecorder = null;
    }

    if (!this.standbyRecorder) {
      this._startStandbyRecorder();
    }
  }

  destroy() {
    this._clearStandbyTimer();

    if (this.standbyRecorder) {
      this.standbyRecorder.abort();
      this.standbyRecorder = null;
    }

    if (this.activeRecorder) {
      this.activeRecorder.abort();
      this.activeRecorder = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }

    this.initialized = false;
  }

  getError() {
    return this.error;
  }

  _startStandbyRecorder() {
    this._clearStandbyTimer();

    if (this.standbyRecorder) {
      this.standbyRecorder.abort();
      this.standbyRecorder = null;
    }

    try {
      const recorder = new ShipmentRecorder(this.stream, this.mimeType, null);
      recorder.start();
      this.standbyRecorder = recorder;

      this._standbyTimer = setTimeout(() => {
        if (this.standbyRecorder === recorder) {
          this._startStandbyRecorder();
        }
      }, VIDEO_RECORDING.STANDBY_CYCLE_MS);
    } catch (err) {
      console.error('Failed to start standby recorder:', err);
    }
  }

  _clearStandbyTimer() {
    if (this._standbyTimer) {
      clearTimeout(this._standbyTimer);
      this._standbyTimer = null;
    }
  }
}
